import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDuration, fmtUsd, parseSpec } from "../api";
import { useUI } from "../components/UI";
import PlanEditor from "../components/PlanEditor";
import { AdvancedFields, SIMPLE_FIELDS, StopFields, roundCostCeiling } from "../components/SettingsForm";

const EXAMPLES = [
  "Fitness influencers in Adana, Turkey, with over 15k followers and more than 15 posts",
  "Personal trainers based in Ankara with 30k to 200k followers",
  "Beauty salons and makeup artists in Izmir with at least 5k followers",
  "Food bloggers in Istanbul posting about street food, over 50k followers",
];

const ago = (iso) => {
  const s = (Date.now() - new Date(iso + "Z").getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso + "Z").toLocaleDateString();
};

const SETTINGS_KEY = "instamatch_settings";

const loadSaved = () => {
  try {
    return JSON.parse(localStorage.getItem(SETTINGS_KEY) || "null");
  } catch {
    return null;
  }
};

const save = (s) => {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    /* private mode */
  }
};

export default function Search() {
  const navigate = useNavigate();
  const { run, toast, advanced } = useUI();
  const [query, setQuery] = useState("");
  const [runs, setRuns] = useState([]);
  const [settings, setSettings] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [spec, setSpec] = useState(null); // reviewed plan; null = let the run plan itself
  const [planning, setPlanning] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/runs");
      setRuns(data);
    } catch {
      /* keep the last list */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    api.get("/runs/settings/defaults").then(({ data }) => {
      setDefaults(data);
      setSettings({ ...data, ...(loadSaved() || {}) });
    });
  }, []);

  const change = (k, v) =>
    setSettings((s) => {
      const next = { ...s, [k]: v };
      save(next);
      return next;
    });

  const ok = query.trim().length >= 3;

  // Simple view runs on defaults: only the goal and the budget are the user's,
  // and with no round limit the run stops on whichever of those two comes first.
  const effective = () =>
    advanced
      ? settings
      : { ...defaults, target_leads: settings.target_leads, budget_usd: settings.budget_usd, max_rounds: 0 };

  const reviewPlan = async () => {
    if (!ok) return;
    setPlanning(true);
    try {
      const { data } = await api.post("/runs/plan", { query: query.trim(), settings: effective() });
      setSpec(data);
    } catch (e) {
      toast(e?.response?.data?.detail || "Planning failed", "error");
    } finally {
      setPlanning(false);
    }
  };

  const start = async (e) => {
    e?.preventDefault();
    if (!ok) return;
    const r = await run(
      async () => (await api.post("/runs", { query: query.trim(), spec: advanced ? spec : null, settings: effective() })).data,
      "Search started"
    );
    if (r?.id) navigate(`/runs/${r.id}`);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) start(e);
  };

  return (
    <div>
      <form className="searchbox" onSubmit={start}>
        <h2>Who are you looking for?</h2>
        <p className="muted">
          Describe the accounts in plain words: niche, city or country, follower and post counts.
          Only public profiles are considered.
        </p>
        <textarea
          rows={3}
          value={query}
          placeholder={EXAMPLES[0]}
          onChange={(e) => {
            setQuery(e.target.value);
            setSpec(null); // a reviewed plan belongs to the query it was made from
          }}
          onKeyDown={onKey}
          autoFocus
        />
        {!spec && !query.trim() && (
          <div className="examples">
            <span className="muted small">Try:</span>
            {EXAMPLES.map((ex) => (
              <button
                type="button"
                key={ex}
                className="chip"
                onClick={() => {
                  setQuery(ex);
                  setSpec(null);
                }}
              >
                {ex}
              </button>
            ))}
          </div>
        )}

        {settings && !advanced && (
          <div className="sf">
            <StopFields value={settings} fields={SIMPLE_FIELDS} onChange={change} />
            <span className="muted small">
              Stops once it has found that many accounts, or when the budget is used up, whichever comes first.
            </span>
          </div>
        )}

        {settings && advanced && (
          <div className="sf">
            <StopFields value={settings} onChange={change} />
            <details className="sf-adv">
              <summary>
                Every stage
                <span className="muted">
                  {" "}· one round costs at most {fmtUsd(roundCostCeiling(settings))} at these settings
                </span>
              </summary>
              <AdvancedFields value={settings} onChange={change} />
              {defaults && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setSettings(defaults);
                    save(defaults);
                  }}
                >
                  Reset to defaults
                </button>
              )}
            </details>
          </div>
        )}

        {advanced && spec && (
          <div className="plan-wrap">
            <div className="row">
              <h3>Plan</h3>
              <span className="muted">{spec.summary}</span>
              <div className="spacer" />
              <button type="button" className="btn" onClick={() => setSpec(null)}>
                Discard plan
              </button>
            </div>
            <PlanEditor spec={spec} onChange={setSpec} />
          </div>
        )}

        <div className="row">
          <div className="spacer" />
          {advanced && !spec && (
            <button type="button" className="btn big" disabled={!ok || planning} onClick={reviewPlan}>
              {planning ? "Planning…" : "Review plan first"}
            </button>
          )}
          <button className="btn primary big" type="submit" disabled={!ok || planning || !settings}>
            {advanced && spec ? "Start with this plan" : "Find accounts"}
          </button>
        </div>
      </form>

      <h3 className="past-title">Recent searches</h3>
      {runs.length === 0 ? (
        <p className="muted">No searches yet.</p>
      ) : (
        <div className="table-scroll">
          <table className="runs-table">
            <thead>
              <tr>
                {advanced && <th className="num">#</th>}
                <th>query</th>
                <th>status</th>
                <th className="num">found</th>
                <th className="num">spent</th>
                {advanced && <th className="num">sourced</th>}
                {advanced && <th className="num">checked</th>}
                {advanced && <th className="num">rounds</th>}
                {advanced && <th className="num">runtime</th>}
                <th className="num">when</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const cfg = parseSpec(r.settings) || {};
                return (
                  <tr key={r.id} className="clickable" onClick={() => navigate(`/runs/${r.id}`)}>
                    {advanced && <td className="num muted">{r.id}</td>}
                    <td className="q-cell" title={r.query}>{r.query}</td>
                    <td className={`status-${r.status}`}>{r.status}</td>
                    <td className="num">
                      <strong>{r.matched}</strong>
                      {cfg.target_leads ? <span className="muted"> / {cfg.target_leads}</span> : null}
                    </td>
                    <td className="num">
                      {cfg.budget_usd || r.spent_usd ? fmtUsd(r.spent_usd) : ""}
                      {cfg.budget_usd ? <span className="muted"> / {fmtUsd(cfg.budget_usd)}</span> : null}
                    </td>
                    {advanced && <td className="num">{r.sourced}</td>}
                    {advanced && <td className="num">{r.evaluated}</td>}
                    {advanced && <td className="num">{r.round}</td>}
                    {advanced && <td className="num">{fmtDuration(r.active_seconds)}</td>}
                    <td className="num muted">{ago(r.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
